import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import LGModalCrud from "../LGCrud/LGModalCrud";
import { useCompany } from "../../contexts/CompanyContext";
import InboundTypeSelector from "../../molecules/Inventory/InboundTypeSelector.tsx";
import InboundPriceUpdateForm from "../../molecules/Inventory/InboundPriceUpdateForm.tsx";
import dayjs from "dayjs";
import { deleteInbound, getInboundList, updateInboundStatus } from "../../api/inbounds/index.ts";
import StatusManager from "../../molecules/Inventory/StatusManager.tsx";
import InboundStatus from "../../molecules/Inventory/InboundStatus.tsx";
import { getUserRoles } from "../../services/auth";

const InboundTable = () => {
  const { selectedCompany } = useCompany();
  const userData = getUserRoles();
  const roles = userData.roles || [];
  const currentUserName = userData.user || "";

  const isAuthorized = (requiredRoles) => {
    return requiredRoles.some((role) => roles.includes(role));
  };

  const setStatusColor = (statusCode) => {
    switch (statusCode) {
      case "INBOUNDS_PENDING":
        return "warning";
      case "INBOUNDS_COMPLETED":
        return "success";
      case "INBOUNDS_REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  const inboundCrudProps = {
    fetchFunction: getInboundList,
    deleteFunction: deleteInbound,
    data: { companyId: selectedCompany.id },
    detailMenu: [
      {
        label: "Cambiar status",
        handler: () => { },
        header: "Cambiar status",
        body: (
          // <InboundStatus updateFunction="woo" />
          <StatusManager
            props={{}}
            updateFunction={updateInboundStatus}
            catalogueName="INBOUNDS"
            fieldName="statusId"
          />
        ),
      },
      // ...(isAuthorized(["auxiliar contable", "admin", "superadmin"]) ? [{
      {
        label: "Actualizar Precio",
        handler: () => { },
        header: "Actualizar Precio de Entrada",
        body: <InboundPriceUpdateForm props={{}} />,
      },
    ],
    columns: [
      { field: "code", headerName: "Folio", flex: 0.1 },
      {
        field: "purchaseOrder",
        headerName: "Orden de Compra",
        flex: 0.2,
        valueGetter: (value, row) => {
          return row.purchaseOrder ? `${row.purchaseOrder.code}` : "";
        },
      },
      {
        field: "purchaseOrder.date",
        headerName: "Fecha de Orden",
        flex: 0.2,
        valueGetter: (value, row) => {
          return row.purchaseOrder && row.purchaseOrder.createdAt
            ? dayjs(row.purchaseOrder.createdAt).format("YYYY-MM-DD")
            : "-";
        },
      },
      { field: "description", headerName: "Descripción", flex: 0.4 },
      {
        field: "inboundItems",
        headerName: "Artículos",
        flex: 0.3,
        renderCell: (params) => {
          const items = params.row.inboundItems || [];
          return (
            <Stack direction="column" spacing={1} flexWrap="wrap">
              {items.map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    {item.quantityDelivered} x
                  </Typography>
                  <Typography variant="caption" noWrap>
                    {item.description ||
                      (item.poItem ? item.poItem.concept : "-")}
                  </Typography>
                </Box>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "workOrders",
        headerName: "Órdenes de Trabajo",
        flex: 0.25,
        renderCell: (params) => {
          const items = params.row.inboundItems || [];
          const workOrders = items
            .filter(item => item.workOrder && item.workOrder.trim() !== "")
            .map(item => item.workOrder)
            .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

          if (workOrders.length === 0) {
            return (
              <Typography variant="caption" color="text.secondary">
                Sin órdenes
              </Typography>
            );
          }

          return (
            <Stack direction="column" spacing={0.5}>
              {workOrders.map((workOrder, index) => (
                <Chip
                  key={index}
                  label={workOrder}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    fontSize: '0.7rem',
                    height: '20px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0.5
                    }
                  }}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "status",
        headerName: "Estatus",
        flex: 0.15,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Chip
              size="small"
              label={params.row.status?.name || "-"}
              color={setStatusColor(params.row.status?.code)}
            />
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Fecha de Entrada",
        flex: 0.2,
        valueFormatter: (value) => {
          if (!value) return "-";
          return dayjs(value).format("YYYY-MM-DD");
        },
      },
    ],
    addModalData: {
      header: "Nuevo Ingreso",
      body: <InboundTypeSelector onSubmit={() => { }} />,
    },
    editModalData: {
      header: "Editar Ingreso",
      body: <InboundTypeSelector onSubmit={() => { }} />,
    },
    actions: ["create", "update", "delete", "detail"],
    canEditRow: (row) => {
      // Only allow editing if the inbound was created by the current user
      return row.createdBy === currentUserName;
    },
    customButtons: [],
  };

  return <LGModalCrud props={inboundCrudProps} />;
};

export default InboundTable;
